import {captureException, ErrorBoundary} from '@sentry/react';
import {useInitFlagsmith} from 'feature-flags';
import {showNotification} from 'platform/components';
import {Box, Show} from 'platform/foundation';

import {lazy, Suspense, useEffect} from 'react';
import {Outlet} from 'react-router-dom';

import {isNil} from 'ramda';

import {useGetTenantIntegrationsAdvancedSearchQuery, useGetTenantQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {useListPermissionExpressionQuery} from '@dms/api/security';
import {browserStorageKey} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {getWorkspaceFromUri} from '@dms/routes';
import {CommonSkeleton, PromoFeaturePopup} from '@dms/shared';

import {isRunningInCypress} from 'shared/web';

import {PromptBridgeProvider} from '../PromptBridgeProvider/PromptBridgeProvider';
import {ApplicationUpdateBoundary} from './components/ApplicationUpdateBoundary';
import {PrefetchBoundary} from './components/PrefetchBoundary';
import {SentryErrorBoundary} from './components/SentryErrorBoundary';
import {useFeatureFlagMockListener} from './hooks/useFeatureFlagMockListener';
import {usePromoFeature} from './hooks/usePromoFeature';
import {sentryInit} from './sentryInit';

const Datadog = lazy(() => import('./components/Datadog'));
const Beamer = lazy(() => import('./components/Beamer'));

export function Initialize() {
  const {workspace} = getWorkspaceFromUri();
  const refreshKey = useFeatureFlagMockListener();

  const {data: userInfo} = useGetCurrentUserInfoQuery();
  const {data: userPermissions} = useListPermissionExpressionQuery();
  // We need to fetch integrations for showing report pages based on tenant settings.
  const {data: tenant} = useGetTenantQuery();
  const {isLoading: isIntegrationsLoading} = useGetTenantIntegrationsAdvancedSearchQuery(
    {tenantId: tenant?.id!},
    {skip: isNil(tenant?.id)}
  );

  const {isOpen, moreInfoRedirectUrl, handleClose} = usePromoFeature();

  const language = userInfo?.settings?.language;

  const {isFlagSmithInitialized, areFeatureFlagsLoadedFromApi} = useInitFlagsmith({
    userEmail: userInfo?.email,
    accessTokenStorageKey: browserStorageKey.ACCESS_TOKEN,
    environmentId: process.env.FLAGSMITH_ENV!,
    flagsmithUrl: `${process.env.FLAGSMITH_URL}/api/v1/`,
  });

  useEffect(() => {
    const dataLayer = {
      environment: process.env.ENV_TYPE,
      workspace: workspace ?? '',
      userEmail: '',
    };
    if (userInfo && userInfo.id && userInfo.email && !isRunningInCypress()) {
      dataLayer.userEmail = userInfo.email;

      sentryInit(userInfo);

      if (!window.dataLayer) {
        captureException(
          'Google Tag Manager (GTM) is not initialized: window.dataLayer is not defined'
        );
        return;
      }
      window.dataLayer.push(dataLayer);
    }

    if (!language || language === localStorage[browserStorageKey.LAST_KNOWN_LANGUAGE]) {
      return;
    }
    i18n
      .changeLanguage(language)
      .then(() => localStorage.setItem(browserStorageKey.LAST_KNOWN_LANGUAGE, language))
      .catch(() => showNotification.error('fatal error - failed to set the application language'));
  }, [workspace, userInfo, language]);

  if (
    !userInfo ||
    !language ||
    !userPermissions ||
    !isFlagSmithInitialized ||
    !tenant ||
    isIntegrationsLoading
  ) {
    return <CommonSkeleton />;
  }

  return (
    <SentryErrorBoundary>
      <ErrorBoundary>
        {areFeatureFlagsLoadedFromApi && (
          <Suspense fallback={null}>
            <Datadog />
          </Suspense>
        )}
      </ErrorBoundary>
      <PrefetchBoundary>
        <ApplicationUpdateBoundary>
          <PromptBridgeProvider>
            <Box key={refreshKey} width="100%" height="100%">
              <Outlet />
            </Box>
          </PromptBridgeProvider>
          <Show whenFeatureEnabled={featureFlags.CORE_NEWS_POPUP}>
            <PromoFeaturePopup
              isOpen={isOpen}
              onClose={handleClose}
              data-testid="promo-feature-popup"
              moreInfoUrl={moreInfoRedirectUrl}
            />
          </Show>
          <Show whenFeatureEnabled={featureFlags.SCRIPT_BEAMER}>
            <Suspense fallback={null}>
              <Beamer />
            </Suspense>
          </Show>
        </ApplicationUpdateBoundary>
      </PrefetchBoundary>
    </SentryErrorBoundary>
  );
}
